"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.UNITS = exports.IkeUnit = void 0;
const core_api_1 = require("@ikegps/core-api");
const DECIMAL = /^(\-)?((?:[1-9]\d*|0)(?:\.\d*)?|\.\d+)([eE][+-]?\d+)?$/;
const FEET_INCH = /^(-)?(\d+)'(?:[ ]?(\d+)")?$/;
const INCHES_IN_FEET = 12;
/** Utility to help working with Distances and Angles */
class IkeUnit {
    /**
     * Case sensitive search for unit type
     * @see IkeUnit.toUnitDistance
     */
    static isUnitDistance(unit) {
        return IkeUnit.UNITS[core_api_1.IkeUnitType.DISTANCE][unit.toLowerCase()] != null;
    }
    /** Case insensitive unit type search */
    static toUnitDistance(unit) {
        let searchUnit = unit.toLowerCase();
        if (IkeUnit.isUnitDistance(searchUnit)) {
            return searchUnit;
        }
        return null;
    }
    /** Add an additional unit to IkeUnit */
    static addUnit(unit) {
        const unitType = unit.type == null ? core_api_1.IkeUnitType.DISTANCE : unit.type;
        IkeUnit.UNITS[unitType][unit.unit] = unit;
        const localDisplay = IkeUnit.DISPLAY_TYPE[unitType];
        for (const displayUnit of unit.display) {
            localDisplay[displayUnit] = unit;
        }
    }
    /** format a angle */
    static displayAngle(value, to, from, decimals) {
        return IkeUnit.display(value, to, from, core_api_1.IkeUnitType.ANGLE, decimals);
    }
    /** Format a area */
    static displayArea(value, to, from) {
        return IkeUnit.display(value, to, from, core_api_1.IkeUnitType.AREA);
    }
    /**
     * Format a value  into a human readable output
     * */
    static display(value, to, from, unitType, decimals) {
        if (unitType == null) {
            unitType = IkeUnit.getUnitType(to);
            if (unitType == null) {
                return '';
            }
        }
        if (decimals == null) {
            decimals = IkeUnit.DECIMALS;
        }
        if (from == null) {
            from = IkeUnit.getBaseType(unitType);
        }
        // Convert the value if required into the output value
        const actualValue = IkeUnit.convert(value, to, from, unitType);
        if (isNaN(actualValue)) {
            return '';
        }
        const unitConversions = IkeUnit.UNITS[unitType];
        const toConversion = unitConversions[to];
        // If the convertor provides a formatting function use it. (Feet/Inches)
        if (toConversion.format && unitType === core_api_1.IkeUnitType.DISTANCE) {
            return toConversion.format(actualValue, unitType);
        }
        return IkeUnit.toFixed(actualValue, decimals) + IkeUnit.getDisplay(to, unitType);
    }
    /** Convert a angle from one angle type to another angle type */
    static convertAngle(value, to, from) {
        return IkeUnit.convert(value, to, from, core_api_1.IkeUnitType.ANGLE);
    }
    static convertArea(value, to, from) {
        return IkeUnit.convert(value, to, from, core_api_1.IkeUnitType.AREA);
    }
    static getUnitType(type) {
        for (const unit of exports.UNITS) {
            if (unit.unit === type) {
                return unit.type;
            }
        }
        return null;
    }
    /**
     * Convert a number from one unit to another
     * @param value value to convert
     * @param to unit type to convert to
     * @param from unit type to convert from
     * @param unitType Type of to and from
     */
    static convert(value, to, from, unitType) {
        if (isNaN(value) || typeof value !== 'number') {
            return NaN;
        }
        // Attempt to determine the base unit type for the to unit type
        if (unitType == null) {
            unitType = IkeUnit.getUnitType(to);
            if (unitType == null) {
                return NaN;
            }
        }
        // Assume the base type if no from is provided
        const baseType = IkeUnit.getBaseType(unitType);
        if (from == null) {
            from = baseType;
        }
        const unitConversions = IkeUnit.UNITS[unitType];
        // If the from is not the base type, convert into the base type first
        if (from !== baseType) {
            var fromConversion = unitConversions[from];
            if (fromConversion == null) {
                return NaN;
            }
            value = value * fromConversion.conversion;
        }
        var toConversion = unitConversions[to];
        if (toConversion == null) {
            return NaN;
        }
        // Convert into the output value
        return value / toConversion.conversion;
    }
    /** Get the display information for the unit, for example " for inches */
    static getDisplay(unit, unitType = core_api_1.IkeUnitType.DISTANCE) {
        const unitConversions = IkeUnit.UNITS[unitType];
        const type = unitConversions[unit];
        if (type == null) {
            return '';
        }
        if (Array.isArray(type.display)) {
            return type.display[0];
        }
        return type.display;
    }
    /** Get the base UnitType */
    static getBaseType(unitType = core_api_1.IkeUnitType.DISTANCE) {
        return IkeUnit.BASE_TYPE[unitType];
    }
    /**
     * Convert the string input into Distance
     *
     * @param input String value of distance.
     * @param unit Unit type to parse from.
     * @returns {number} Distance in Meters.
     */
    static parse(input, unit) {
        if (input == null) {
            return NaN;
        }
        let test = input.trim();
        if (test.length === 0) {
            return NaN;
        }
        let currentUnit, unitPortion;
        const unitMatches = test.match(/[^0-9]+$/);
        if (unitMatches) {
            unitPortion = unitMatches[0];
            var currentUnitObj = IkeUnit.DISPLAY_TYPE[core_api_1.IkeUnitType.DISTANCE][unitPortion];
            if (currentUnitObj != null) {
                currentUnit = currentUnitObj.unit;
            }
        }
        if (currentUnit) {
            test = test.substr(0, test.length - unitPortion.length);
        }
        else {
            currentUnit = unit;
        }
        const matches = test.match(DECIMAL);
        if (matches) {
            var sign = matches[1] == null ? 1 : -1;
            return sign * IkeUnit.convert(parseFloat(matches[2]), IkeUnit.METER, currentUnit);
        }
        return IkeUnit.parseFeetInch(input);
    }
    static getUnits(unitType) {
        return IkeUnit.UNITS[unitType];
    }
    // Better ToFixed implementation
    // @see http://stackoverflow.com/questions/10015027/javascript-tofixed-not-rounding
    static toFixed(num, precision) {
        return (+(Math.round(+(num + 'e' + precision)) + 'e' + -precision)).toFixed(precision);
    }
    /**
     * Round a number to the provided precision
     *
     * @param num Number to round
     * @param precision Number of decimal places to keep
     */
    static round(num, precision) {
        return (Math.round((num * Math.pow(10, precision)).toFixed(precision - 1)) / Math.pow(10, precision));
    }
    /** Split inches into feet and inches  */
    static splitFeetInch(inches) {
        /* tslint:disable:no-bitwise */
        return {
            feet: ~~(inches / INCHES_IN_FEET), // Truncate the remainder
            inches: inches % INCHES_IN_FEET
        };
        /* tslint:enable:no-bitwise */
    }
    /**
     * Join feet & inches into inches
     * @param feet Number of feet (Feet)
     * @param inches Number of inches (Inches)
     * @returns Total size (Inches)
     */
    static joinFeetInch(feet, inches) {
        return feet * INCHES_IN_FEET + inches;
    }
    /**
     * Format a number into feet and inches
     *
     * @param value value to convert
     * @param unitType Unit that value is in
     * @returns Value formatted in feet and inches (1' 6")
     */
    static formatFeetInch(value, unitType) {
        var inches = Math.round(IkeUnit.convert(value, IkeUnit.INCH, IkeUnit.FEET, unitType));
        var feetInches = this.splitFeetInch(inches);
        var display = `${Math.abs(feetInches.feet)}'`;
        if (value < 0) {
            display = `-${display}`;
        }
        if (feetInches.inches !== 0) {
            return `${display} ${Math.abs(feetInches.inches) + IkeUnit.getDisplay(IkeUnit.INCH, unitType)}`;
        }
        return display;
    }
    /**
     * Attempt to parse Feet/Inches from a string
     *
     * @returns Parsed value or NaN (Meters)
     */
    static parseFeetInch(input) {
        var matches = input.match(FEET_INCH);
        if (matches == null) {
            return NaN;
        }
        // Feet and inches
        var sign = matches[1] == null ? 1 : -1;
        var feet = parseFloat(matches[2]);
        var inches = matches[3] ? parseFloat(matches[3]) : 0;
        if (inches > 11) {
            return NaN;
        }
        var feetM = IkeUnit.convert(feet, IkeUnit.METER, IkeUnit.FEET);
        var inchM = IkeUnit.convert(inches, IkeUnit.METER, IkeUnit.INCH);
        return (feetM + inchM) * sign;
    }
}
exports.IkeUnit = IkeUnit;
// Distance
IkeUnit.FEET = core_api_1.IkeUnitDistance.FEET;
IkeUnit.METER = core_api_1.IkeUnitDistance.METER;
IkeUnit.CENTIMETER = core_api_1.IkeUnitDistance.CENTIMETER;
IkeUnit.MILLIMETER = core_api_1.IkeUnitDistance.MILLIMETER;
IkeUnit.INCH = core_api_1.IkeUnitDistance.INCH;
// Angles
IkeUnit.RADIAN = core_api_1.IkeUnitAngle.RADIAN;
IkeUnit.DEGREE = core_api_1.IkeUnitAngle.DEGREE;
/** Default distance type to use */
IkeUnit.DEFAULT = IkeUnit.METER;
/** Default formatting length */
IkeUnit.DECIMALS = 2;
IkeUnit.UNITS = {
    [core_api_1.IkeUnitType.ANGLE]: {},
    [core_api_1.IkeUnitType.DISTANCE]: {},
    [core_api_1.IkeUnitType.AREA]: {}
};
IkeUnit.DISPLAY_TYPE = {
    [core_api_1.IkeUnitType.ANGLE]: {},
    [core_api_1.IkeUnitType.DISTANCE]: {},
    [core_api_1.IkeUnitType.AREA]: {}
};
/** Each unit type has a base type that all values are stored as */
IkeUnit.BASE_TYPE = {
    [core_api_1.IkeUnitType.ANGLE]: core_api_1.IkeUnitAngle.RADIAN,
    [core_api_1.IkeUnitType.DISTANCE]: core_api_1.IkeUnitDistance.METER,
    [core_api_1.IkeUnitType.AREA]: core_api_1.IkeUnitDistance.METER
};
// Load the unit definitions.
exports.UNITS = [
    { unit: core_api_1.IkeUnitDistance.METER, conversion: 1, display: ['m'], label: 'Meters', type: core_api_1.IkeUnitType.DISTANCE },
    { unit: core_api_1.IkeUnitDistance.CENTIMETER, conversion: 0.01, label: 'Centimeters', display: ['cm'], type: core_api_1.IkeUnitType.DISTANCE },
    { unit: core_api_1.IkeUnitDistance.MILLIMETER, conversion: 0.001, display: ['mm'], type: core_api_1.IkeUnitType.DISTANCE },
    {
        unit: core_api_1.IkeUnitDistance.FEET,
        conversion: 0.3048,
        display: ['\'', 'ft', 'feet'],
        label: 'Feet/Inches',
        format: (value, unitType) => IkeUnit.formatFeetInch(value, unitType),
        type: core_api_1.IkeUnitType.DISTANCE
    }, {
        unit: core_api_1.IkeUnitDistance.INCH,
        conversion: 0.0254,
        display: ['"', 'in'],
        label: 'Inches',
        type: core_api_1.IkeUnitType.DISTANCE
    },
    // Angle
    { unit: core_api_1.IkeUnitAngle.RADIAN, conversion: 1, display: ['rad'], type: core_api_1.IkeUnitType.ANGLE },
    { unit: core_api_1.IkeUnitAngle.DEGREE, conversion: Math.PI / 180, display: ['°'], type: core_api_1.IkeUnitType.ANGLE },
    // Area
    // TODO Area should not really share the same unit name as distance.
    { unit: core_api_1.IkeUnitDistance.METER, conversion: 1, display: ['m²'], type: core_api_1.IkeUnitType.AREA },
    { unit: core_api_1.IkeUnitDistance.CENTIMETER, conversion: 0.0001, display: ['cm²'], type: core_api_1.IkeUnitType.AREA },
    { unit: core_api_1.IkeUnitDistance.MILLIMETER, conversion: 1e-6, display: ['mm²'], type: core_api_1.IkeUnitType.AREA },
    { unit: core_api_1.IkeUnitDistance.INCH, conversion: 0.00064516, display: ['in²'], type: core_api_1.IkeUnitType.AREA },
    { unit: core_api_1.IkeUnitDistance.FEET, conversion: 0.09290304, display: ['ft²'], type: core_api_1.IkeUnitType.AREA }
];
exports.UNITS.forEach(u => IkeUnit.addUnit(u));
//# sourceMappingURL=unit.js.map